import React, { useEffect, useMemo, useRef, useState } from "react";
import "./AstrologyClock.css";
import useGeoLocation from "./hooks/useGeoLocation";

const TAMIL_RAASI_12 = [
  "மேஷம்",
  "ரிஷபம்",
  "மிதுனம்",
  "கடகம்",
  "சிம்மம்",
  "கன்னி",
  "துலாம்",
  "விருச்சிகம்",
  "தனுசு",
  "மகரம்",
  "கும்பம்",
  "மீனம்",
];

const TAMIL_NAKSHATRA_27 = [
  "அசுவினி",
  "பரணி",
  "கார்த்திகை",
  "ரோகிணி",
  "மிருகசீரிடம்",
  "திருவாதிரை",
  "புனர்பூசம்",
  "பூசம்",
  "ஆயில்யம்",
  "மகம்",
  "பூரம்",
  "உத்திரம்",
  "அஸ்தம்",
  "சித்திரை",
  "ஸ்வாதி",
  "விசாகம்",
  "அனுஷம்",
  "கேட்டை",
  "மூலம்",
  "பூராடம்",
  "உத்திராடம்",
  "திருவோணம்",
  "அவிட்டம்",
  "சதயம்",
  "பூரட்டாதி",
  "உத்திரட்டாதி",
  "ரேவதி",
];

function clampIndex(idx, len) {
  if (typeof idx !== "number" || Number.isNaN(idx)) return null;
  if (idx < 0 || idx >= len) return null;
  return idx;
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, r, startAngleDeg, endAngleDeg) {
  const start = polarToCartesian(cx, cy, r, endAngleDeg);
  const end = polarToCartesian(cx, cy, r, startAngleDeg);
  const largeArc = endAngleDeg - startAngleDeg <= 180 ? "0" : "1";
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

function formatTime(h, m) {
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  return `${hh}:${mm}`;
}

export default function AstrologyClock() {
  const panchangRef = useRef(null);
  const [now, setNow] = useState(() => new Date());
  const { coords, status: geoStatus } = useGeoLocation();
  const [astro, setAstro] = useState({
    raasiIndex: null,
    nakshatraIndex: null,
    raasiName: "",
    nakshatraName: "",
    computedAt: null,
  });

  // 1) Time tick (every second)
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // 3) Panchang calculator instance
  useEffect(() => {
    // Keep CommonJS require to avoid bundler/interop issues.
    // eslint-disable-next-line global-require
    const { MhahPanchang } = require("mhah-panchang");
    const obj = new MhahPanchang();

    // Override names to Tamil.
    obj.setMhahConstant("Raasi", "name", TAMIL_RAASI_12);
    obj.setMhahConstant("Nakshatra", "name", TAMIL_NAKSHATRA_27);

    panchangRef.current = obj;
  }, []);

  // 4) Recompute Raasi/Nakshatra periodically (every minute) or when location changes
  useEffect(() => {
    let cancelled = false;

    const compute = () => {
      const obj = panchangRef.current;
      if (!obj || !coords) return;

      try {
        const res = obj.calendar(new Date(), coords.latitude, coords.longitude);
        const raasiIdx = clampIndex(res?.Raasi?.ino, 12);
        const nakIdx = clampIndex(res?.Nakshatra?.ino, 27);

        if (cancelled) return;
        setAstro({
          raasiIndex: raasiIdx,
          nakshatraIndex: nakIdx,
          raasiName: res?.Raasi?.name || "",
          nakshatraName: res?.Nakshatra?.name || "",
          computedAt: new Date(),
        });
      } catch (e) {
        if (cancelled) return;
        setAstro((prev) => ({ ...prev, computedAt: new Date() }));
      }
    };

    compute();
    const id = window.setInterval(compute, 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [coords]);

  const time = useMemo(() => {
    const h = now.getHours();
    const m = now.getMinutes();
    const s = now.getSeconds();
    const hourAngle = ((h % 12) + m / 60 + s / 3600) * 30;
    const minuteAngle = (m + s / 60) * 6;
    const secondAngle = s * 6;
    return { h, m, s, hourAngle, minuteAngle, secondAngle };
  }, [now]);

  // SVG geometry
  const cx = 200;
  const cy = 200;
  const rRaasi = 128;
  const rNakTickOuter = 188;
  const rNakTickInner = 172;
  const rDial = 150;

  const activeRaasiAngle = astro.raasiIndex == null ? null : astro.raasiIndex * 30;
  const activeNakAngle = astro.nakshatraIndex == null ? null : astro.nakshatraIndex * (360 / 27);
  const nakSeg = 360 / 27;
  const activeNakArc =
    activeNakAngle == null
      ? null
      : arcPath(cx, cy, 180, activeNakAngle, activeNakAngle + nakSeg);

  return (
    <div className="astroClock" aria-label="Astrology clock showing Raasi and Nakshatra">
      <div className="astroClock__header">
        <div className="astroClock__title">ஜோதிடக் கடிகாரம்</div>
        <div className="astroClock__subtitle">
          {geoStatus.state === "requesting" && "இடம் பெறுகிறது…"}
          {geoStatus.state === "granted" && "GPS இடம் பயன்படுத்தப்படுகிறது"}
          {geoStatus.state === "fallback" && "Chennai (fallback) இடம் பயன்படுத்தப்படுகிறது"}
        </div>
      </div>

      <div className="astroClock__content">
        <div className="astroClock__dialWrap">
          <svg className="astroClock__dial" viewBox="0 0 400 400" role="img" aria-label="Analog clock dial">
            <defs>
              <radialGradient id="dialGlow" cx="50%" cy="50%" r="60%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.08)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </radialGradient>
            </defs>

            {/* Background */}
            <circle cx={cx} cy={cy} r="194" className="astroClock__outer" />
            <circle cx={cx} cy={cy} r="160" className="astroClock__inner" />
            <circle cx={cx} cy={cy} r="194" fill="url(#dialGlow)" />

            {/* Active Nakshatra segment highlight */}
            {activeNakArc && <path d={activeNakArc} className="astroClock__nakHighlight" />}

            {/* Nakshatra ticks */}
            {TAMIL_NAKSHATRA_27.map((name, i) => {
              const angle = i * (360 / 27);
              const p1 = polarToCartesian(cx, cy, rNakTickInner, angle);
              const p2 = polarToCartesian(cx, cy, rNakTickOuter, angle);
              const active = astro.nakshatraIndex === i;
              return (
                <g key={name}>
                  <title>{name}</title>
                  <line
                    x1={p1.x}
                    y1={p1.y}
                    x2={p2.x}
                    y2={p2.y}
                    className={active ? "astroClock__nakTick astroClock__nakTick--active" : "astroClock__nakTick"}
                  />
                </g>
              );
            })}

            {/* Raasi labels (12) */}
            {TAMIL_RAASI_12.map((name, i) => {
              const angle = i * 30;
              const pos = polarToCartesian(cx, cy, rRaasi, angle);
              const active = astro.raasiIndex === i;
              return (
                <g key={name}>
                  <text
                    x={pos.x}
                    y={pos.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className={active ? "astroClock__raasi astroClock__raasi--active" : "astroClock__raasi"}
                  >
                    {name}
                  </text>
                </g>
              );
            })}

            {/* Hour ticks */}
            {Array.from({ length: 60 }).map((_, i) => {
              const angle = i * 6;
              const isHour = i % 5 === 0;
              const r1 = isHour ? rDial - 18 : rDial - 10;
              const r2 = rDial;
              const p1 = polarToCartesian(cx, cy, r1, angle);
              const p2 = polarToCartesian(cx, cy, r2, angle);
              return (
                <line
                  key={i}
                  x1={p1.x}
                  y1={p1.y}
                  x2={p2.x}
                  y2={p2.y}
                  className={isHour ? "astroClock__tick astroClock__tick--hour" : "astroClock__tick"}
                />
              );
            })}

            {/* Active Raasi marker */}
            {activeRaasiAngle != null && (
              <circle
                cx={polarToCartesian(cx, cy, rDial + 10, activeRaasiAngle).x}
                cy={polarToCartesian(cx, cy, rDial + 10, activeRaasiAngle).y}
                r="4.5"
                className="astroClock__raasiMarker"
              />
            )}

            {/* Clock hands */}
            <g transform={`rotate(${time.hourAngle} ${cx} ${cy})`}>
              <line x1={cx} y1={cy} x2={cx} y2={cy - 72} className="astroClock__hand astroClock__hand--hour" />
            </g>
            <g transform={`rotate(${time.minuteAngle} ${cx} ${cy})`}>
              <line
                x1={cx}
                y1={cy}
                x2={cx}
                y2={cy - 104}
                className="astroClock__hand astroClock__hand--minute"
              />
            </g>
            <g transform={`rotate(${time.secondAngle} ${cx} ${cy})`}>
              <line
                x1={cx}
                y1={cy + 16}
                x2={cx}
                y2={cy - 116}
                className="astroClock__hand astroClock__hand--second"
              />
            </g>
            <circle cx={cx} cy={cy} r="6" className="astroClock__pin" />
          </svg>
        </div>

        <div className="astroClock__panel">
          <div className="astroClock__now">
            <div className="astroClock__time">{formatTime(time.h, time.m)}</div>
            <div className="astroClock__current">
              <span className="astroClock__label">இப்போது:</span>{" "}
              <span className="astroClock__value">{astro.raasiName || "—"}</span>{" "}
              <span className="astroClock__sep">•</span>{" "}
              <span className="astroClock__value">{astro.nakshatraName || "—"}</span>
            </div>
          </div>

          <div className="astroClock__meta">
            <div className="astroClock__metaRow">
              <span className="astroClock__metaKey">இடம்</span>
              <span className="astroClock__metaVal">{coords?.label || "—"}</span>
            </div>
            <div className="astroClock__metaRow">
              <span className="astroClock__metaKey">புதுப்பிப்பு</span>
              <span className="astroClock__metaVal">
                {astro.computedAt ? astro.computedAt.toLocaleTimeString("en-IN") : "—"}
              </span>
            </div>
            {geoStatus.message ? <div className="astroClock__hint">{geoStatus.message}</div> : null}
          </div>

          <div className="astroClock__legend" aria-label="Nakshatra list">
            <div className="astroClock__legendTitle">27 நட்சத்திரம்</div>
            <div className="astroClock__legendGrid">
              {TAMIL_NAKSHATRA_27.map((name, i) => {
                const active = astro.nakshatraIndex === i;
                return (
                  <div
                    key={name}
                    className={active ? "astroClock__legendItem astroClock__legendItem--active" : "astroClock__legendItem"}
                  >
                    <span className="astroClock__legendIdx">{i + 1}</span>
                    <span className="astroClock__legendName">{name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

